/** @jsxImportSource @emotion/react */
// see - https://www.figma.com/file/pv2HV6DTSKY612VWeYWYdL/CE22-Design-System?node-id=79%3A1998
import type { ReactElement, ReactNode } from 'react';
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCircleExclamation,
  faCircleXmark,
} from '@fortawesome/free-solid-svg-icons';
import type { Interpolation, Theme } from '@emotion/react';
import type { Theme as MuiTheme } from '@mui/material';
import { Box } from '@mui/material';
import type { ITheme } from '../../styles/theme';
import Typography, { TypographyVariant } from '../Typography';
import { useSettingBasedOnBreakpoints } from '../../../hooks/use-setting-based-on-breakpoints';
import type { IPropsWithTestId } from '../../../constants/data-element-tracking-ids';
import { ELEMENT_ID_DATA_TEST_ATTRIBUTE } from '../../../constants/data-element-tracking-ids';
import { BoxWithId } from '../BoxWithId/BoxWithId';

interface IProps extends IPropsWithTestId {
  title?: ReactNode;
  description: ReactNode;
  variant: 'default' | 'error' | 'success' | 'warning' | 'info';
  iconElement?: ReactElement;
  theme: ITheme;
  muiTheme: MuiTheme;
  additionalStyles?: Interpolation<Theme>;
}

const AttentionBox: React.FC<IProps> = ({
  title,
  description,
  variant = 'default',
  iconElement,
  dataElementId,
  theme,
  muiTheme,
  additionalStyles = {},
}) => {
  const { colors } = theme;
  const { isMobile } = useSettingBasedOnBreakpoints(muiTheme);

  const variants = {
    default: {
      styles: {
        backgroundColor: colors.grayscale.inactiveGrey2,
      },
      titleColor: colors.grayscale.black,
      titleIcon: faCircleExclamation,
      iconColor: colors.grayscale.inactiveGrey1,
    },
    error: {
      styles: {
        backgroundColor: colors.negativeBackground,
      },
      titleColor: colors.negative,
      titleIcon: faCircleXmark,
      iconColor: colors.negative,
    },
    success: {
      styles: { backgroundColor: colors.positiveBackground },
      titleColor: colors.positiveHover,
      titleIcon: faCircleCheck,
      iconColor: colors.positiveHover,
      iconElementBackgroundColor: 'rgba(62, 166, 141, 0.15)',
    },
    warning: {
      styles: { backgroundColor: colors.bannerYellow },
      titleColor: colors.negative,
      titleIcon: faCircleExclamation,
      iconColor: colors.negative,
    },
    info: {
      styles: { backgroundColor: colors.neutralBackgroundColor },
      titleColor: colors.neutralHover,
      titleIcon: faCircleExclamation,
      iconColor: colors.neutralHover,
    },
  };
  const variantsForIconElement = {
    default: {
      iconElementBackgroundColor: undefined,
    },
    success: {
      iconElementBackgroundColor: 'rgba(62, 166, 141, 0.15)',
    },
  };
  const variantProperties = variants[variant];
  const variantForIconElementProperties =
    variant === 'success'
      ? variantsForIconElement.success
      : variantsForIconElement.default;

  const borderRadius = '4px';

  const attentionBoxClasses = {
    ...(title
      ? {}
      : {
        display: 'flex',
        alignItems: 'flex-start',
      }),
    alignItems: 'stretch',
    borderRadius,
    padding: !iconElement ? '24px' : 0,
    ...variantProperties.styles,
  };

  const textStyles = {
    padding: iconElement ? `16px ${isMobile ? 16 : 24}px` : undefined,
  };

  return (
    <BoxWithId
      css={[attentionBoxClasses, additionalStyles]}
      role="note"
      dataElementId={dataElementId}
    >
      <div
        css={
          title
            ? {
              display: 'flex',
              alignItems: 'center',
              marginBottom: '8px',
            }
            : ''
        }
      >
        {iconElement ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
              padding: '20px 24px',
              background:
                variantForIconElementProperties?.iconElementBackgroundColor,
              borderTopLeftRadius: borderRadius,
              borderBottomLeftRadius: borderRadius,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                maxWidth: '60px',
                maxHeight: '60px',
              }}
            >
              {iconElement}
            </Box>
          </Box>
        ) : (
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              height: '24px',
            }}
          >
            <FontAwesomeIcon
              icon={variantProperties.titleIcon}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...{
                [ELEMENT_ID_DATA_TEST_ATTRIBUTE]: `${dataElementId}-info-icon`,
              }}
              css={{
                marginRight: '8px',
                lineHeight: '24px',
                fontSize: '20px',
                color: variantProperties.iconColor,
              }}
            />
          </div>
        )}
        {!!title && (
          <Typography
            variant={TypographyVariant.LARGE_BODY_BOLD}
            color={variantProperties.titleColor}
            theme={theme}
            additionalStyles={textStyles}
          >
            {title}
          </Typography>
        )}
      </div>
      <Typography
        variant={TypographyVariant.MEDIUM_BODY_REGULAR}
        color={variant === 'default' ? colors.grayscale.black : undefined}
        theme={theme}
        additionalStyles={textStyles}
      >
        {description}
      </Typography>
    </BoxWithId>
  );
};

export default AttentionBox;
